import React, { useRef, useEffect, useState } from 'react'

import {
	useParams,
	useLocation,
	useRouteMatch,
	Route,
	Switch,
	BrowserRouter as Router,
	useHistory,
} from 'react-router-dom'

import { useFetchList, openedTask } from '../../store'
import { ClinicalProcess, Document } from 'models'
import ClinicalProcessHeader from '../../components/ClinicalProcessHeader/ClinicalProcessHeader'
import ClinicalAppointmentsController from './ClinicalAppointmentsController'
import ClinicalOpinionsController from './ClinicalOpinionsController'
import ClinicalRequisitionsController from './ClinicalRequisitionsController'
import ClinicalCommentsThreadController from './ClinicalCommentsThreadController'
import AppointmentsDisabilitiesController from '../AppointmentsDisabilities/AppointmentsDisabilitiesController'
import TicketsController from './TicketsController'

import { ClinicalHistoryController } from './ClinicalHistoryController'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Tabs from '@jimmyboys/liita-ui/dist/components/Tabs'
import RouteTransition from '@jimmyboys/liita-ui/dist/components/RouteTransition'

import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import { useRecoilValue } from 'recoil'

import styles from './ClinicalProcess.module.sass'
import SearchFiltersMenu from './SearchFiltersMenu/SearchFiltersMenu'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import MedicalOpinion from 'models/MedicalOpinion'
import AllocationHistory from 'models/AllocationHistory'

const tabs = (match, i18n, searchParams = '') => {
	return [
		{
			to: `${match.url}${searchParams}`,
			name: i18n`All`,
		},
		{
			to: `${match.url}/appointments${searchParams}`,
			name: i18n`Appointments`,
		},
		{
			to: `${match.url}/requisitions${searchParams}`,
			name: i18n`Requisitions`,
		},
		{
			to: `${match.url}/comments${searchParams}`,
			name: i18n`Comments`,
		},
		{
			to: `${match.url}/clinical-evaluations-predictions${searchParams}`,
			name: i18n`Clinical Opinion`,
		},
		{
			to: `${match.url}/tickets${searchParams}`,
			name: i18n`Tickets`,
		},
		{
			to: `${match.url}/disabilities${searchParams}`,
			name: i18n`Disabilities`,
		},
	]
}

const ClinicalProcessController = () => {
	const params = useParams()
	const location = useLocation()
	const [queryParams, searchParams] = useQueryParams(location)
	const { dispatch } = useDrawerContext()
	const match = useRouteMatch()
	const { openFile, i18n, screenSize } = useGlobal()
	const currentOpenedTask = useRecoilValue(openedTask)
	const innerRouterRef = useRef(null)
	const history = useHistory()
	const processUri = `/clinical_processes/${params.clinicalProcessId}`
	const participationUri = `/clinical_processes/${params.clinicalProcessId}/participation_file`
	const latestTriageUri = `/clinical_processes/${params.clinicalProcessId}/latest_triage`
	const coordinatorAllocationHistoryUri = `/clinical_processes/${params.clinicalProcessId}/coordinator_allocation_history`
	const lockedCoordinatorsUri = `/clinical_processes/${params.clinicalProcessId}/check_coordinator_lock`

	const { contents } = useFetchList(
		[
			processUri,
			participationUri,
			latestTriageUri,
			coordinatorAllocationHistoryUri,
			lockedCoordinatorsUri,
		],
		[
			ClinicalProcess.fromJSON,
			Document.fromJSON,
			MedicalOpinion.fromJSON,
			AllocationHistory.fromJSON,
		]
	)

	const [
		processData,
		participationFile,
		latestTriage,
		coordinatorAllocationHistory,
		lockedCoordinators,
	] = contents

	const process = processData || location.state || {}

	useEffect(() => {
		if (process?.insurancePolicy) {
			sessionStorage.setItem(
				'processExternalId',
				process.insurancePolicy.insuranceCompany.id
			)
		}
		//eslint-disable-next-line
	}, [process])

	const [tabSelected, setTabSelected] = useState(null)
	const [selectedTabValue, setSelectedTabValue] = useState(null)

	const tabsOptions = [
		{
			value: 'all',
			text: i18n`All`,
		},
		{
			value: 'appointments',
			text: i18n`Appointments`,
		},
		{
			value: 'requisitions',
			text: i18n`Requisitions`,
		},
		{
			value: 'comments',
			text: i18n`Comments`,
		},
		{
			value: 'threads',
			text: i18n`Comments`,
		},
		{
			value: 'clinical_evaluations_predictions',
			text: i18n`Clinical Opinion`,
		},
		{
			value: 'tickets',
			text: i18n`Tickets`,
		},
		{
			value: 'disabilities',
			text: i18n`Disabilities`,
		},
	]

	const tabEndpoints = {
		all: `${match.url}${searchParams}`,
		appointments: `${match.url}/appointments${searchParams}`,
		requisitions: `${match.url}/requisitions${searchParams}`,
		comments: `${match.url}/comments${searchParams}`,
		threads: `${match.url}/threads${searchParams}`,
		clinical_evaluations_predictions: `${match.url}/clinical-evaluations-predictions${searchParams}`,
		tickets: `${match.url}/tickets${searchParams}`,
		disabilities: `${match.url}/disabilities${searchParams}`,
	}

	useEffect(() => {
		let pathname = location.pathname
		if (location.pathname.slice(-1) === '/') {
			pathname = location.pathname.slice(0, -1)
		}

		const tab = pathname.split('/').pop().replace(new RegExp('-', 'g'), '_')

		const selectedTab =
			tab === params.clinicalProcessId || tab === '' ? 'all' : tab

		const valueSelectedTab = `${match.url}${
			selectedTab === 'all'
				? ''
				: `/${selectedTab.replace(new RegExp('_', 'g'), '-')}`
		}${searchParams}`

		setTabSelected(selectedTab)
		setSelectedTabValue(valueSelectedTab)
	}, [location.pathname, match.url, params.clinicalProcessId, searchParams])

	useEffect(() => {
		const unListen = history.listen((location) => {
			if (innerRouterRef.current) {
				innerRouterRef.current.history.replace(location)
			}
		})
		return () => {
			unListen && unListen()
		}
	}, [history])

	useEffect(() => {
		let timeout
		if (
			currentOpenedTask?.openTask?.processId === process?.id &&
			['Triage', 'TriageOpinion'].includes(
				currentOpenedTask?.openTask?.type
			) &&
			participationFile
		) {
			timeout = setTimeout(() => {
				openFile(participationFile, false)
			}, 800)
		}
		return () => {
			clearTimeout(timeout)
		}
		//eslint-disable-next-line
	}, [participationFile?.id, openFile, currentOpenedTask, process?.id])

	useEffect(() => {
		if (params?.clinicalProcessId && queryParams?.comment) {
			dispatch({
				action: 'OPEN_DRAWER',
				payload: {
					data: {
						processId: params.clinicalProcessId,
						id: queryParams.comment,
					},
					type: 'CommentThreadDrawer',
				},
			})
		}
	}, [dispatch, params?.clinicalProcessId, queryParams?.comment])

	return (
		<>
			<Widget
				paddings={['small', 'normal', '']}
				className={styles['header-container']}
				applyMaxWidth
			>
				<ClinicalProcessHeader
					hero='true'
					processId={process?.id}
					state={process?.state}
					clinicalStatus={process?.clinicalStatus}
					injured={process?.injured}
					accident={process?.accident}
					insurancePolicy={process?.insurancePolicy}
					mainMedicalDiagnosis={process?.mainMedicalDiagnosis}
					provider={process?.provider}
					info={process?.info}
					trustClaimsManager={process?.trustClaimsManager}
					companyClaimsManager={process?.companyClaimsManager}
					clinicCoordinator={process?.clinicCoordinator}
					initialCoordinator={process?.initialCoordinator}
					ippCoordinator={process?.ippCoordinator}
					tempId={process?.temporaryProcessId}
					category={process?.processCategory}
					participationFile={participationFile}
					processTag={process?.tag}
					acutePhase={latestTriage?.acutePhase}
					coordinatorAllocationHistory={coordinatorAllocationHistory}
					lockedCoordinators={lockedCoordinators}
					processTags={process?.processTags}
				/>
			</Widget>
			<div />
			<Widget
				data-sticky
				className={styles['inner-tabs']}
				margins={['small', '', '']}
				applyMaxWidth
			>
				<HStack
					align='center'
					justify='space-between'
					gap={'large'}
					flex={[
						[0, 0],
						[0, 1],
					]}
				>
					{screenSize === 'small' ? (
						<Dropdown
							className={styles['tabs-dropdown']}
							value={tabSelected}
							options={tabsOptions}
							onChange={(e) => {
								setTabSelected(e.target.value)
								history.push(tabEndpoints[e.target.value])
							}}
						/>
					) : (
						<Tabs
							selected={selectedTabValue}
							justify='flex-start'
							className={styles['section-tabs']}
							tabs={tabs(match, i18n, searchParams)}
						/>
					)}
					<SearchFiltersMenu processId={params.clinicalProcessId} />
				</HStack>
			</Widget>
			<Widget
				className={styles['clinical-list']}
				paddings={['small', 'normal']}
				applyMaxWidth
			>
				<Router ref={innerRouterRef}>
					<RouteTransition>
						<Switch>
							<Route path={`${match.path}`} exact>
								<ClinicalHistoryController />
							</Route>
							<Route path={`${match.path}/appointments`} exact>
								<ClinicalAppointmentsController />
							</Route>
							<Route path={`${match.path}/requisitions`} exact>
								<ClinicalRequisitionsController />
							</Route>
							<Route path={`${match.path}/comments`} exact>
								<ClinicalCommentsThreadController />
							</Route>
							<Route
								path={`${match.path}/clinical-evaluations-predictions`}
								exact
							>
								<ClinicalOpinionsController />
							</Route>
							<Route path={`${match.path}/tickets`} exact>
								<TicketsController />
							</Route>
							<Route path={`${match.path}/disabilities`} exact>
								<AppointmentsDisabilitiesController />
							</Route>
						</Switch>
					</RouteTransition>
				</Router>
			</Widget>
		</>
	)
}

export default ClinicalProcessController
